/**
 * Schedule Presets — Sprint 4.7.4
 *
 * Catálogo de agendas pré-definidas que cobrem casos comuns na frota
 * Fibra Net. Cada preset gera um objeto `schedule` compatível com o
 * editor (ScheduleEditor) e com o backend (ScheduleEntryIn).
 *
 * Formato:
 *   {
 *     id, name, icon, description,
 *     schedule: { enabled, entries: [...], exceptions: [...] }
 *   }
 *
 * Termos técnicos preservados (NÃO traduzir):
 *   - days: 0..6 (0=Dom, 6=Sáb)
 *   - action: "relay_on" | "relay_off" | "relay_reboot" | "esp_reboot"
 *   - time (modo pulso) / on_time + off_time (modo janela)
 *   - duration_s (modo pulso, 1..300)
 *
 * Backend é fonte de verdade para validação — estes presets já
 * atendem ScheduleEntryIn._validate_mode mas se um operador editar
 * antes de aplicar, validação client+server segue normal.
 */

// Helpers para construir entries — mantém presets legíveis.
function pulse(days, time, duration_s, action = "relay_reboot") {
  return { days, time, duration_s, action, enabled: true };
}

function onoff(days, on_time, off_time, action = "relay_on") {
  return { days, on_time, off_time, action, enabled: true };
}

// 0=Dom, 1=Seg, 2=Ter, 3=Qua, 4=Qui, 5=Sex, 6=Sáb
const WEEKDAYS = [1, 2, 3, 4, 5];          // Seg-Sex
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];    // Dom-Sáb
const MWF = [1, 3, 5];                     // Seg, Qua, Sex
const SUNDAY = [0];                        // Apenas domingo

/**
 * Lista de presets disponíveis. A ORDEM aqui determina a ordem
 * dos cards na UI.
 *
 * IDs são estáveis (não traduzir, não renumerar) — futuras sprints
 * podem se basear neles (ex: marcar "qual preset gerou esta agenda").
 */
export const SCHEDULE_PRESETS = [
  {
    id: "hotspot_commercial",
    icon: "🏢",
    nameKey: "schedulePresets.hotspot.name",
    descKey: "schedulePresets.hotspot.desc",
    schedule: {
      enabled: true,
      entries: [pulse(WEEKDAYS, "04:30", 30, "relay_reboot")],
      exceptions: [],
    },
  },
  {
    id: "residential",
    icon: "🏠",
    nameKey: "schedulePresets.residential.name",
    descKey: "schedulePresets.residential.desc",
    schedule: {
      enabled: true,
      entries: [pulse(ALL_DAYS, "05:00", 30, "relay_reboot")],
      exceptions: [],
    },
  },
  {
    id: "server_24x7",
    icon: "🖥",
    nameKey: "schedulePresets.server.name",
    descKey: "schedulePresets.server.desc",
    schedule: {
      enabled: true,
      entries: [pulse(SUNDAY, "03:00", 60, "relay_reboot")],
      exceptions: [],
    },
  },
  {
    id: "pop_radio",
    icon: "🌐",
    nameKey: "schedulePresets.popRadio.name",
    descKey: "schedulePresets.popRadio.desc",
    schedule: {
      enabled: true,
      entries: [pulse(ALL_DAYS, "04:00", 45, "relay_reboot")],
      exceptions: [],
    },
  },
  {
    id: "maintenance_window",
    icon: "🔧",
    nameKey: "schedulePresets.maintenance.name",
    descKey: "schedulePresets.maintenance.desc",
    // Janela de manutenção: desliga às 02:00 e religa às 02:10.
    // Modela como 1 entry ON/OFF com action=relay_off
    // (semântica do backend: desliga em on_time, religa em off_time).
    // Reset: na verdade ScheduleEntryIn aceita {on_time, off_time,
    // action="relay_off"}, e o firmware interpreta como "fica nesse
    // estado durante essa janela".
    schedule: {
      enabled: true,
      entries: [onoff(MWF, "02:00", "02:10", "relay_off")],
      exceptions: [],
    },
  },
];

/**
 * Resumo legível de uma agenda preset para o card.
 * Retorna string curta — formato esperado pelo PresetCard.
 *
 * Exemplos:
 *   "Seg–Sex · 04:30 · pulse 30s relay_reboot"
 *   "Todos os dias · 05:00 · pulse 30s relay_reboot"
 *   "Seg, Qua, Sex · 02:00→02:10 · janela relay_off"
 */
import { isPulseEntry, DAY_NAMES } from "./ScheduleEditor.jsx";

const DAY_LETTERS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function formatDays(days) {
  if (!Array.isArray(days) || days.length === 0) return "—";
  // Detecta padrões comuns para texto curto
  const set = new Set(days);
  if (set.size === 7) return "Todos os dias";
  // Seg-Sex (workdays)
  if (set.size === 5 && [1,2,3,4,5].every((d) => set.has(d))) return "Seg–Sex";
  // Apenas 1 dia
  if (set.size === 1) return DAY_NAMES[[...set][0]];
  // Lista enxuta
  return [...set].sort().map((d) => DAY_LETTERS_SHORT[d]).join(", ");
}

/**
 * Resumo da PRIMEIRA entry (presets têm 1 entry — verdade pra todos
 * os 5 presets listados). Se um preset futuro tiver N>1 entries, o
 * formatter mostra "+N-1 entradas..." no fim.
 */
export function formatPresetSummary(schedule) {
  if (!schedule || !Array.isArray(schedule.entries) || schedule.entries.length === 0) {
    return "Sem entradas.";
  }
  const e = schedule.entries[0];
  const days = formatDays(e.days);
  let body;
  if (isPulseEntry(e)) {
    body = `${e.time} · pulso ${e.duration_s}s · ${e.action || "relay_reboot"}`;
  } else {
    body = `${e.on_time}→${e.off_time} · janela · ${e.action || "relay_on"}`;
  }
  const extra =
    schedule.entries.length > 1
      ? ` (+${schedule.entries.length - 1})`
      : "";
  return `${days} · ${body}${extra}`;
}

/**
 * Clonagem profunda do preset.schedule. Garantir que o operador
 * editar o resultado NÃO mute o catálogo original (caso o usuário
 * volte ao mesmo preset depois).
 */
export function clonePresetSchedule(preset) {
  if (!preset || !preset.schedule) {
    return { enabled: false, entries: [], exceptions: [] };
  }
  return {
    enabled: preset.schedule.enabled === true,
    entries: Array.isArray(preset.schedule.entries)
      ? preset.schedule.entries.map((e) => ({ ...e, days: [...(e.days || [])] }))
      : [],
    exceptions: Array.isArray(preset.schedule.exceptions)
      ? [...preset.schedule.exceptions]
      : [],
  };
}

/**
 * Lookup por id (estável). Retorna preset ou null.
 */
export function findPresetById(id) {
  return SCHEDULE_PRESETS.find((p) => p.id === id) || null;
}
